import { useQueryCache } from '@pinia/colada';
import { defineStore } from 'pinia';

import { useAlertsStore } from '@tallo/design-system';
import { getActivityFeedRootQueryKey } from '@tallo/investor/activity-feed';
import { getApplicationBundlesQueryKey } from '@tallo/investor/applications';
import { getIntelligentEscalationsQueryKey } from '@tallo/investor/intelligent-escalations';
import { showingsQueryKey } from '@tallo/investor/showing';

import { AgentChatMessageDto, AgentChatThreadDto } from '../models/agent-chat.interfaces';
import agentChatSocketService, { AgentChatSocketEvent } from '../services/agent-chat-socket.service';
import agentChatService from '../services/agent-chat.service';

/**
 * UI and live-message state for the agent panel. Fetching the thread belongs to the thread
 * query (queries/agent-chat-thread.query.ts), mounted with the panel — this store folds those
 * pages together with socket pushes and the owner's own sends into one message list.
 */
export const useAgentChatStore = defineStore('agentChat', {
  state: (): {
    isOpen: boolean;
    conversationId: string | null;
    messages: AgentChatMessageDto[];
    hasMore: boolean;
    loadingOlder: boolean;
    sending: boolean;
    awaitingReply: boolean;
    agentProcessing: boolean;
    socketConnected: boolean;
  } => ({
    isOpen: false,
    conversationId: null,
    messages: [],
    hasMore: false,
    loadingOlder: false,
    sending: false,
    awaitingReply: false,
    agentProcessing: false,
    socketConnected: false,
  }),
  getters: {
    // awaitingReply covers the gap between our POST and the server's processing event, so the
    // typing dots appear the moment the owner sends and stay until the reply lands or fails.
    agentTyping(): boolean {
      return this.awaitingReply || this.agentProcessing;
    },
  },
  actions: {
    openPanel(): void {
      this.isOpen = true;
      this.connectSocket();
    },

    closePanel(): void {
      this.isOpen = false;
    },

    togglePanel(): void {
      if (this.isOpen) {
        this.closePanel();
      } else {
        this.openPanel();
      }
    },

    /**
     * Folds a freshly fetched newest page into the thread. When the page no longer overlaps
     * what is cached (the thread grew past a page while the panel was away), the cache is
     * replaced wholesale — merging across the gap would render a thread with a hole in the
     * middle.
     */
    applyThreadPage(thread: AgentChatThreadDto): void {
      this.conversationId = thread.conversationId;

      const cachedNewestId = this.messages[this.messages.length - 1]?.id ?? 0;
      const fetchedOldest = thread.messages[0];

      if (this.messages.length === 0 || !fetchedOldest || fetchedOldest.id <= cachedNewestId) {
        if (this.messages.length === 0) {
          this.hasMore = thread.hasMore;
        }
        thread.messages.forEach((message) => this.upsertMessage(message));
      } else {
        this.messages = thread.messages;
        this.hasMore = thread.hasMore;
      }
    },

    async loadOlderMessages(): Promise<void> {
      if (!this.hasMore || this.loadingOlder || this.messages.length === 0) {
        return;
      }

      this.loadingOlder = true;

      try {
        const thread = await agentChatService.getThread(undefined, this.messages[0].id);

        this.messages = [...thread.messages, ...this.messages];
        this.hasMore = thread.hasMore;
      } catch {
        useAlertsStore().error('Could not load older messages');
      } finally {
        this.loadingOlder = false;
      }
    },

    async sendMessage(content: string): Promise<boolean> {
      const trimmed = content.trim();

      if (!trimmed || this.sending) {
        return false;
      }

      this.sending = true;
      this.awaitingReply = true;

      try {
        const message = await agentChatService.sendMessage(trimmed);

        this.upsertMessage(message);
        return true;
      } catch {
        this.awaitingReply = false;
        useAlertsStore().error('Could not send the message');
        return false;
      } finally {
        this.sending = false;
      }
    },

    /** Logout chokepoint (appInitService.reset): the next account must not see this thread or its socket. */
    reset(): void {
      agentChatSocketService.disconnect();
      this.$reset();
    },

    connectSocket(): void {
      if (this.socketConnected) {
        return;
      }

      const socket = agentChatSocketService.connect();

      socket.on(AgentChatSocketEvent.MESSAGE, (message: AgentChatMessageDto) => this.upsertMessage(message));
      socket.on(AgentChatSocketEvent.PROCESSING, (payload: { processing: boolean }) => {
        this.agentProcessing = payload.processing;
      });
      socket.on(AgentChatSocketEvent.ERROR, () => {
        this.awaitingReply = false;
        this.agentProcessing = false;
        useAlertsStore().error('Tallo could not reply — please try again');
      });

      this.socketConnected = true;
    },

    /** Messages arrive from the thread query, the socket mirror, and the owner's sends — dedupe by id. */
    upsertMessage(message: AgentChatMessageDto): void {
      if (this.messages.some((existing) => existing.id === message.id)) {
        return;
      }

      this.messages = [...this.messages, message].sort((a, b) => a.id - b.id);

      if (message.isSystem) {
        this.awaitingReply = false;
        this.refreshDashboardData();
      }
    },

    /**
     * Every agent reply may have acted on the account (booked a tour, answered an escalation,
     * edited a listing), so the rail cards, counters, and feed re-fetch to stay in sync with
     * what the agent just said it did.
     */
    refreshDashboardData(): void {
      const queryCache = useQueryCache();

      queryCache.invalidateQueries({ key: getIntelligentEscalationsQueryKey() });
      queryCache.invalidateQueries({ key: [showingsQueryKey] });
      queryCache.invalidateQueries({ key: getApplicationBundlesQueryKey() });
      queryCache.invalidateQueries({ key: getActivityFeedRootQueryKey() });
    },
  },
});
